import React, { useState, useEffect } from 'react';
import api from '../services/api';
import CredencialCard from './CredencialCard';

// Recibir props del Dashboard
const Credenciales = ({
  onOpenCreateModal,
  onOpenEditModal,
  refreshTrigger,
}) => {
  const [credenciales, setCredenciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    departamento: '',
    tipo_empleado: '',
    nombre_completo: '',
    nuevo_ingreso: '',
  });
  const [viewCardOpen, setViewCardOpen] = useState(false);
  const [viewingCredencial, setViewingCredencial] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchCredenciales();
  }, [filters, refreshTrigger]); // Agregar refreshTrigger como dependencia

  const fetchCredenciales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.departamento) params.departamento = filters.departamento;
      if (filters.tipo_empleado) params.tipo_empleado = filters.tipo_empleado;
      if (filters.nombre_completo)
        params.nombre_completo = filters.nombre_completo;
      if (filters.nuevo_ingreso !== '')
        params.nuevo_ingreso = filters.nuevo_ingreso === 'true';
      params.limit = 1000;

      const response = await api.get('/credenciales', { params });
      setCredenciales(response.data);
      setCurrentPage(0);
    } catch (error) {
      console.error('Error fetching credenciales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCard = (credencial) => {
    setViewingCredencial(credencial);
    setViewCardOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta credencial?')) return;

    try {
      await api.delete(`/credenciales/${id}`);
      fetchCredenciales();
    } catch (error) {
      console.error('Error deleting credencial:', error);
      alert('Error al eliminar la credencial');
    }
  };

  // Usar las props para abrir modales
  const handleEdit = (credencial) => {
    onOpenEditModal(credencial);
  };

  const handleCreate = () => {
    onOpenCreateModal();
  };

  const resetFilters = () => {
    setFilters({
      departamento: '',
      tipo_empleado: '',
      nombre_completo: '',
      nuevo_ingreso: '',
    });
  };

  const paginatedCredenciales = credenciales.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const totalPages = Math.ceil(credenciales.length / itemsPerPage);

  return (
    <div className="animate-fadeInUp">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          Gestión de Credenciales
        </h2>
        <button
          onClick={handleCreate}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] px-4 py-2 flex items-center space-x-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Nueva Credencial</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 mb-6 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={filters.nombre_completo}
            onChange={(e) =>
              setFilters({ ...filters, nombre_completo: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          />
          <input
            type="text"
            placeholder="Departamento"
            value={filters.departamento}
            onChange={(e) =>
              setFilters({ ...filters, departamento: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          />
          <select
            value={filters.tipo_empleado}
            onChange={(e) =>
              setFilters({ ...filters, tipo_empleado: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          >
            <option value="" className="bg-gray-800">
              Todos los tipos
            </option>
            <option value="ADMINISTRATIVO" className="bg-gray-800">
              Administrativo
            </option>
            <option value="OPERATIVO" className="bg-gray-800">
              Operativo
            </option>
          </select>
          <div className="flex space-x-2">
            <select
              value={filters.nuevo_ingreso}
              onChange={(e) =>
                setFilters({ ...filters, nuevo_ingreso: e.target.value })
              }
              className="flex-1 px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
            >
              <option value="" className="bg-gray-800">
                Nuevo Ingreso
              </option>
              <option value="true" className="bg-gray-800">
                Sí
              </option>
              <option value="false" className="bg-gray-800">
                No
              </option>
            </select>
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20"
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      ) : (
        <>
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden border border-white/20">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/20">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                      Nombre Completo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                      Departamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                      Nuevo Ingreso
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-200 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paginatedCredenciales.map((credencial) => (
                    <tr
                      key={credencial.id}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          {credencial.nombre_completo}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-blue-200">
                          {credencial.departamento || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            credencial.tipo_empleado === 'ADMINISTRATIVO'
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                              : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                          }`}
                        >
                          {credencial.tipo_empleado || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {credencial.nuevo_ingreso ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Nuevo
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-white/10 text-white/60 border border-white/20">
                            -
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewCard(credencial)}
                          className="text-green-400 hover:text-green-300 mr-3 transition-colors"
                        >
                          Ver
                        </button>
                        <button
                          onClick={() => handleEdit(credencial)}
                          className="text-blue-400 hover:text-blue-300 mr-3 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(credencial.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
              >
                Anterior
              </button>
              <span className="text-sm text-blue-200">
                Página {currentPage + 1} de {totalPages} ({credenciales.length}{' '}
                registros)
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
                }
                disabled={currentPage === totalPages - 1}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

      {/* CredencialCard - Este sí puede estar aquí porque es un overlay */}
      <CredencialCard
        isOpen={viewCardOpen}
        onClose={() => setViewCardOpen(false)}
        credencial={viewingCredencial}
      />
    </div>
  );
};

export default Credenciales;
